import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormInput, FormSection, FormSelect, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import { useFetchCategoriesAllQuery } from "../../../stores/api/categories.api";
import {
  useCreateSubCategoryMutation,
  useUpdateSubCategoryMutation,
} from "../../../stores/api/subCategories.api";
import { buildJson } from "../../../stores/api/helpers/payload.helper";

const EMPTY = {
  category_id: "",
  name: "",
  slug: "",
  description: "",
  status: 1,
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
};

const toSlug = (str) => str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const CreateProductSubCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.subCategory;

  const { data: categoriesRaw = [] } = useFetchCategoriesAllQuery();
  const categories = useMemo(
    () => (categoriesRaw || []).map((c) => ({ value: String(c.id), label: c.name })),
    [categoriesRaw]
  );

  const [createSubCategory, { isLoading: creating }] = useCreateSubCategoryMutation();
  const [updateSubCategory, { isLoading: updating }] = useUpdateSubCategoryMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          category_id: String(details.category_id) || "",
          name: details.name || "",
          slug: details.slug || "",
          description: details.description || "",
          status: details.status ?? 1,
          meta_title: details.meta_title || "",
          meta_description: details.meta_description || "",
          meta_keywords: details.meta_keywords || "",
        }
      : {}),
  });

  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "name" && !details) next.slug = toSlug(value);
      return next;
    });
  };

  const resetForm = () => setFormData(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = buildJson({
        ...formData,
        status: formData.status ? 1 : 0,
      });

      if (details) {
        await updateSubCategory({ id: details.id, body }).unwrap();
      } else {
        await createSubCategory(body).unwrap();
      }

      ToastMessage({ type: "success", title: "Success", message: details ? "Sub-category updated!" : "Sub-category created!" });
      if (!details) navigate(ROUTE_PATHS.PRODUCT_SUB_CATEGORY);
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Sub Category" : "Create Sub Category"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Sub Category Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Parent Category" required options={categories} value={formData.category_id} onChange={(val) => setFormData((prev) => ({ ...prev, category_id: val ?? "" }))} />
            <FormInput label="Name" name="name" value={formData.name} onChange={(val) => setField("name", val)} placeholder="Enter sub-category name" />
            <FormInput label="Slug" name="slug" value={formData.slug} onChange={(val) => setField("slug", val)} placeholder="auto-generated-from-name" />
            <FormInput label="Description" name="description" value={formData.description} onChange={(val) => setFormData((prev) => ({ ...prev, description: val }))} placeholder="Short description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>

        <FormSection title="SEO / Meta">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Meta Title" name="meta_title" value={formData.meta_title} onChange={(val) => setFormData((prev) => ({ ...prev, meta_title: val }))} placeholder="SEO title" />
            <FormInput label="Meta Keywords" name="meta_keywords" value={formData.meta_keywords} onChange={(val) => setFormData((prev) => ({ ...prev, meta_keywords: val }))} placeholder="keyword1, keyword2" />
            <FormInput label="Meta Description" name="meta_description" value={formData.meta_description} onChange={(val) => setFormData((prev) => ({ ...prev, meta_description: val }))} placeholder="SEO description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>

        <FormSection title="Status">
          <FormSwitch label="Status" checked={formData.status === 1} onChange={(val) => setFormData((prev) => ({ ...prev, status: val ? 1 : 0 }))} />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" disabled={loading} onClick={() => { resetForm(); navigate(ROUTE_PATHS.PRODUCT_SUB_CATEGORY); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200">
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} className="!bg-orange-500 !text-white hover:!bg-orange-600" loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Sub Category" : "Create Sub Category"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateProductSubCategory;
